import { useEffect, useState } from 'react';

import { fetchHealthGoals, fetchUserHealthGoals, type HealthGoal } from '@/modules/api';
import { useRulesEngine } from '@/modules/rulesEngine';

type UseHealthGoals = {
  /** Catalog of selectable goals, in backend (curated) order. */
  goals: HealthGoal[];
  /** The user's already-saved goal urns, for prefill. */
  initialSelection: string[];
  isLoading: boolean;
  error: unknown;
};

// Fetches the goals catalog + the user's current selection on mount and holds
// them in local state — the catalog is screen-scoped, so (like
// useHealthConditions / useLegalDocuments) it doesn't belong in
// RulesEngineProvider. Unlike conditions we keep the backend's order rather
// than sorting alphabetically: the goal list is curated/ranked server-side.
export function useHealthGoals(): UseHealthGoals {
  const { user } = useRulesEngine();
  const userUrn = user?.urn;

  const [goals, setGoals] = useState<HealthGoal[]>([]);
  const [initialSelection, setInitialSelection] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    Promise.all([
      fetchHealthGoals(),
      userUrn ? fetchUserHealthGoals(userUrn) : Promise.resolve<string[]>([]),
    ])
      .then(([catalog, selected]) => {
        if (cancelled) return;
        setGoals(catalog);
        setInitialSelection(selected);
        setIsLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err);
        setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userUrn]);

  return { goals, initialSelection, isLoading, error };
}
